import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { useOnError } from 'src/utils/errors';
import { ICON_MAP } from 'src/utils/constants';
import { GeneralASError } from 'src/registry/types/axios';
import { cn } from 'src/utils';
import { SubscriptionItem } from 'src/features/subscriptionItems/utils/types';
import {
    subscriptionItemFormSchema,
    SubscriptionItemFormSchemaValues,
} from 'src/features/subscriptionItems/utils/formSchema';
import { useGetSubscriptionItem } from 'src/features/subscriptionItems/api/useGetSubscriptionItem';
import { useParams } from 'react-router-dom';
import SubscriptionItemForm from 'src/features/subscriptionItems/components/forms/SubscriptionItemForm';

interface SubscriptionItemFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedSubscriptionItem?: SubscriptionItem;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (
        data: SubscriptionItemFormSchemaValues,
        onSuccess: () => void,
        onError: (error: GeneralASError) => void,
    ) => void;
}

function SubscriptionItemFormModal<TData>({
    selectedSubscriptionItem,
    isPending,
    children,
    className,
    icon,
    title,
    subtitle,
    mutate,
}: SubscriptionItemFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { subscriptionId = '' } = useParams();

    const { data: subscriptionItem } = useGetSubscriptionItem({
        subscriptionId,
        itemId: selectedSubscriptionItem?.id ?? '',
        // We only want to fetch if we're in edit
        config: { enabled: !!selectedSubscriptionItem?.id && isModalOpen },
    });
    const onError = useOnError();

    const defaultValues = React.useMemo(() => {
        return {
            start_date: subscriptionItem?.start_date ?? undefined,
            end_date: subscriptionItem?.end_date ?? undefined,
            service_plan_item: subscriptionItem?.service_plan_item.id ?? undefined,
            is_active: subscriptionItem?.is_active ?? undefined,
        };
    }, [subscriptionItem]);

    const form = useForm<SubscriptionItemFormSchemaValues>({
        resolver: zodResolver(subscriptionItemFormSchema),
        defaultValues,
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [form, isModalOpen, defaultValues]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();
        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: SubscriptionItemFormSchemaValues) => {
            const payload = {
                ...values,
                subscription: subscriptionId,
            };
            mutate(
                { ...payload },
                () => setIsModalOpen(false),
                (error) => onError(error, form.setError),
            );
        },
        [mutate, onError, form.setError, subscriptionId],
    );

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={cn('ml-auto', className)}>
                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={icon} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>{subtitle}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <SubscriptionItemForm form={form} onSubmit={onSubmit} subscriptionItem={subscriptionItem} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedSubscriptionItem && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default SubscriptionItemFormModal;
